/**
 * 게이지의 입력 — 쓴 양을 총량으로 나눈 0~1. 둘 중 하나를 모르거나 총량이 0이면 비율이 없어
 * `Gauge` 가 빈 고리와 `—` 를 보인다. 글자는 `ui/format.ts` 의 `fraction` 이 만든다.
 */
export function share(used: number | null | undefined, total: number | null | undefined): number | null {
  if (used === null || used === undefined || total === null || total === undefined || total <= 0) return null
  return used / total
}

// ─── 고리의 모양 ─────────────────────────────────────────────────────────────
//
// 고리는 recharts 의 RadialBarChart 로 그리던 것을 같은 모양의 SVG 경로로 옮긴 것이다. 아래 수는 그 차트의 설정
// (`innerRadius`·`outerRadius` 백분율·`barSize`·`cornerRadius`)을 recharts 3.8.0 이 계산하던 그대로 풀어 둔 값이고,
// 경로 함수는 recharts `shape/Sector` 의 계산을 옮긴 것이다 — 근사가 아니다. 테스트가 브라우저에서 뜬 경로와
// recharts 의 `Sector` 가 내는 경로에 대조한다(`frontend/src/components/gauge.test.ts`).
//
// 반지름의 유도: 차트는 사방 여백 5 라 가장 큰 반지름이 (상자 − 10)/2 이고, 반지름 축은 [inner%, 100%] 의 띠 하나다.
// 막대 두께가 띠보다 크거나 같으면 띠의 0.9 를 여백 없이 쓰고, 작으면 (띠 − 두께)/2 를 버림한 만큼 안에서 시작한다.
// 모서리는 Sector 가 두께의 절반으로 자른다 — compact 의 3 은 2.565 가 되어 끝이 반원이고, large 의 4 는 그대로라 반원이 아니다.

export type Ring = { box: number; r0: number; r1: number; corner: number }

/** 목록의 48px 고리 — 반지름 최대 19, 띠 [13.3, 19](70%), 두께 6 ≥ 띠 5.7 이라 0.9 × 5.7 = 5.13. */
export const RING_COMPACT: Ring = { box: 48, r0: 13.3, r1: 18.43, corner: 3 }
/** 상세의 128px 고리 — 반지름 최대 59, 띠 [43.66, 59](74%), 두께 10 < 띠 15.34 라 ⌊2.67⌋ = 2 안에서 시작. */
export const RING_LARGE: Ring = { box: 128, r0: 45.66, r1: 55.66, corner: 4 }

const RADIAN = Math.PI / 180

/** recharts 의 `round` — 소수 넷째 자리, -0 은 0. */
function round(n: number): number {
  const r = Math.round(n * 10 ** 4) / 10 ** 4
  return Object.is(r, -0) ? 0 : r
}

type Point = { x: number; y: number }

/** 각도는 3시에서 반시계 방향이다(recharts 의 `polarToCartesian`). */
function polar(c: number, radius: number, angle: number): Point {
  return { x: c + Math.cos(-RADIAN * angle) * radius, y: c + Math.sin(-RADIAN * angle) * radius }
}

const xy = (p: Point) => `${round(p.x)},${round(p.y)}`

/**
 * 퍼센트의 끝 각도 — 각도 축은 [0, 100] 을 [90, −270] 로 잇는 d3 선형 척도다. 90 이 12시이고 줄어들면 시계 방향이다.
 * 식을 d3 의 보간과 같은 모양으로 둔다: 같은 수를 다른 식으로 셈하면 넷째 자리 반올림이 드물게 갈린다.
 */
function endAngle(pct: number): number {
  const t = pct / 100
  return 90 * (1 - t) + -270 * t
}

/** 모서리 없는 부채꼴(Sector 의 `getSectorPath`) — 한 바퀴는 359.999° 로 자른다. */
function sectorPath(c: number, r0: number, r1: number, start: number, end: number): string {
  const delta = Math.sign(end - start) * Math.min(Math.abs(end - start), 359.999)
  const tempEnd = start + delta
  const large = +(Math.abs(delta) > 180)
  return (
    `M ${xy(polar(c, r1, start))} A ${round(r1)},${round(r1)},0, ${large},${+(start > tempEnd)}, ${xy(polar(c, r1, tempEnd))}` +
    ` L ${xy(polar(c, r0, tempEnd))} A ${round(r0)},${round(r0)},0, ${large},${+(start <= tempEnd)}, ${xy(polar(c, r0, start))} Z`
  )
}

/** 모서리를 굴리는 원이 반지름 `radius` 의 호와 각도 `angle` 의 반지름 선에 닿는 두 점(Sector 의 `getTangentCircle`, 모서리는 안쪽). */
function tangent(c: number, radius: number, angle: number, sign: number, external: boolean, corner: number) {
  const centerRadius = corner * (external ? 1 : -1) + radius
  const theta = Math.asin(corner / centerRadius) / RADIAN
  return {
    circle: polar(c, radius, angle + sign * theta),
    line: polar(c, centerRadius * Math.cos(theta * RADIAN), angle),
    theta,
  }
}

/** 모서리 있는 부채꼴(Sector 의 `getSectorWithCorner`) — 모서리가 들어갈 각이 모자라면 모서리 없는 쐐기다. */
function cornerPath(c: number, r0: number, r1: number, corner: number, start: number, end: number): string {
  const sign = Math.sign(end - start)
  const os = tangent(c, r1, start, sign, false, corner)
  const oe = tangent(c, r1, end, -sign, false, corner)
  const outerArc = Math.abs(start - end) - os.theta - oe.theta
  if (outerArc < 0) return sectorPath(c, r0, r1, start, end)
  const cr = round(corner)
  const cw = +(sign < 0)
  const is = tangent(c, r0, start, sign, true, corner)
  const ie = tangent(c, r0, end, -sign, true, corner)
  const innerArc = Math.abs(start - end) - is.theta - ie.theta
  return (
    `M ${xy(os.line)} A${cr},${cr},0,0,${cw},${xy(os.circle)} A${round(r1)},${round(r1)},0,${+(outerArc > 180)},${cw},${xy(oe.circle)} A${cr},${cr},0,0,${cw},${xy(oe.line)}` +
    ` L${xy(ie.line)} A${cr},${cr},0,0,${cw},${xy(ie.circle)} A${round(r0)},${round(r0)},0,${+(innerArc > 180)},${+(sign > 0)},${xy(is.circle)} A${cr},${cr},0,0,${cw},${xy(is.line)}Z`
  )
}

/** 빈 고리(궤도) — 값과 무관한 한 바퀴. recharts 는 이것을 모서리 없이 그린다. */
export function trackPath(ring: Ring): string {
  return sectorPath(ring.box / 2, ring.r0, ring.r1, 90, -270)
}

/**
 * 0~100 퍼센트의 막대 — 12시에서 시계 방향. 0 이면 그릴 것이 없어 null 이다(recharts 의 Sector 도 시작과 끝이 같으면
 * 그리지 않는다 — 선 끝을 둥글린 획으로 그리면 0 에서 점이 남는다). 100 은 모서리 없는 한 바퀴다.
 */
export function ringPath(ring: Ring, pct: number): string | null {
  if (pct <= 0) return null
  const c = ring.box / 2
  if (pct >= 100) return sectorPath(c, ring.r0, ring.r1, 90, -270)
  return cornerPath(c, ring.r0, ring.r1, Math.min(ring.corner, (ring.r1 - ring.r0) / 2), 90, endAngle(pct))
}
